import "reactflow/dist/style.css";
import { React, useEffect, useRef, useState, useCallback } from "react";
import { GET, POST } from "../../api/requests";
import { SortableTree } from "./SortableTree";
import { useParams } from "react-router-dom";
import { useQuery } from "react-query";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import {
  solarizedlight,
  darcula,
} from "react-syntax-highlighter/dist/esm/styles/prism";

import Interface from "./Interface";
import { Bird, ClipboardCopy, Sprout, Trees } from "lucide-react";
import { ColorRing } from "react-loader-spinner";
import { SparklesIcon } from "@heroicons/react/20/solid";

export default function FlowBuilder() {
  // Whenever a value isf updated in the editor, create a copy we can work on
  // using the node editor
  // TODO: Merge edits instead of overwriting the whole thing
  // Default to pre-recorded steps
  const [nodes, setNodes] = useState([]);
  const [items, writeItems] = useState([]);
  const [automation, setAutomation] = useState(null);
  const [inputObject, setInputObject] = useState(null);
  const [editAutomationPrompt, setEditAutomationPrompt] = useState(null);
  const [automationLoading, setIsAutomationLoading] = useState(false);
  const { slug } = useParams();

  const {
    data: recording,
    isError,
    isLoading,
    error,
  } = useQuery(
    ["recording", slug],
    () => {
      return GET(`/recordings/${slug}`);
    },
    {
      enabled: !!slug, // Only run this if we have a slug in the URL
      staleTime: Infinity, // The data will never be considered stale
      cacheTime: Infinity, // The data will stay in cache indefinitely
      refetchOnWindowFocus: false, // The data will not be refetched when window regains focus
      refetchOnReconnect: false, // The data will not be refetched when internet connection is regained
      retry: false, // The query will not retry on failure
    }
  );

  const editAutomationWithPrompt = async () => {
    setIsAutomationLoading(true);
    const response = await POST(`/recordings/${slug}/automations`, {
      recording: items,
      prompt: editAutomationPrompt,
      currentAutomation: automation,
    });
    if (response.playwrightAutomationScript) {
      setAutomation(response.playwrightAutomationScript);
    }
    if (response.inputObject) {
      setInputObject(response.inputObject);
    }
    setIsAutomationLoading(false);
    setEditAutomationPrompt("");
  };

  const createAutomationFromRecording = async () => {
    setIsAutomationLoading(true);
    const response = await POST(`/recordings/${slug}/automations`, {
      recording: items,
    });
    console.log({ response });
    if (response.playwrightAutomationScript) {
      setAutomation(response.playwrightAutomationScript);
    }
    if (response.inputObject) {
      setInputObject(response.inputObject);
    }
    setIsAutomationLoading(false);
  };

  useEffect(() => {
    if (recording && recording.length > 0) {
      let initialNodes = [];
      let i = 0;
      for (let step of recording) {
        initialNodes.push({
          id: step.id,
          children: [],
          // Wrap all the data in the data field for this sortableTree so we can manipulate
          // independent from the fields that affect the tree structure
          data: step,
        });
        i++;
      }
      console.log({ initialNodes });
      setNodes(initialNodes);
    }
  }, [recording]);

  // When a selector is replaced with a visual anchor, point to a URL
  return (
    <div
      style={{ height: "100vh", overflowY: "scroll" }}
      className="grid grid-cols-3 bg-auto-bg text-auto-white"
    >
      <div className="border-r border-auto-mid overflow-y-scroll">
        <div
          style={{
            zIndex: 10,
            background: "linear-gradient(180deg, #252624, transparent)",
          }}
          className="flex items-center pt-3 pb-4 px-4  sticky top-0"
        >
          <img style={{ width: 26 }} src={"/symbol_logo.png"} />
          <h1 className="font-space font-semibold text-md text-auto-white ml-2 -mt-0.5">
            Workflow builder
          </h1>
        </div>

        <div
          style={{ height: "100%", paddingBottom: 240 }}
          className="px-3 -mt-2"
        >
          {nodes.length > 0 && (
            <SortableTree writeItems={writeItems} defaultItems={nodes} />
          )}
        </div>

        <div className="bg-auto-bg p-4 sticky bottom-0">
          <button
            disabled={automationLoading}
            className={`w-full text-auto-white  bg-auto-white bg-opacity-40 shadow-md rounded-lg hover:bg-opacity-30 text-sm font-semibold py-2 ${
              automationLoading ? "bg-opacity-30" : ""
            }`}
            onClick={() => createAutomationFromRecording(items)}
          >
            Generate automation
          </button>
        </div>
      </div>
      <div className="border-r relative border-auto-mid bg-auto-darkest overflow-y-scroll">
        <div
          style={{
            zIndex: 10,
            background: "linear-gradient(180deg, #1C1D1B, transparent)",
          }}
          className="flex items-center pt-3 pb-4 px-4  sticky top-0"
        >
          <h1 className="font-space font-semibold text-md text-auto-white ml-2 -mt-0.5">
            Automation code
          </h1>
        </div>

        {automation ? (
          <div>
            <div className="px-4 pb-16" style={{ fontSize: 14 }}>
              <div
                className={`rounded-xl overflow-hidden bg-auto-mid ${
                  automationLoading ? "opacity-40" : ""
                }`}
              >
                <SyntaxHighlighter
                  customStyle={{
                    backgroundColor: "rgba(0,0,0,0)",
                    fontSize: 12,
                  }}
                  language="javascript"
                  style={darcula}
                >
                  {automation}
                </SyntaxHighlighter>
              </div>
            </div>
            <div
              style={{ zIndex: 99 }}
              className="border-t bottom-0 sticky bg-auto-darkest w-full border-auto-mid mt-4 flex items-center px-4 py-3 justify-between"
            >
              <SparklesIcon className="text-auto-light w-4" />
              <input
                type="text"
                className="block w-full flex-1 ring-0 outline-0 border-none outline-none bg-transparent py-0.5 text-auto-white shadow-sm  placeholder:text-auto-light focus:ring-0 text-xs sm:leading-6"
                value={editAutomationPrompt}
                disabled={automationLoading}
                onChange={(e) => setEditAutomationPrompt(e.target.value)}
                onKeyPress={(event) => {
                  if (event.key === "Enter") {
                    editAutomationWithPrompt();
                  }
                }}
                placeholder="Enter a prompt to edit this automation"
              />
              {automationLoading && (
                <div className="mx-auto">
                  <ColorRing
                    visible={true}
                    height="20"
                    width="20"
                    ariaLabel="blocks-loading"
                    colors={[
                      "#D2B5F9",
                      "#D2B5F9",
                      "#D2B5F9",
                      "#D2B5F9",
                      "#D2B5F9",
                    ]}
                  />
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="flex flex-col space-around items-center w-full mt-20">
            <Bird size={42} strokeWidth={1.5} color={"#474945"} />
            <p className="text-xs text-auto-light mt-4">
              No automation generated yet
            </p>
          </div>
        )}
      </div>
      <div className="bg-auto-bg h-full px-4 overflow-y-scroll">
        <div
          style={{
            zIndex: 10,
            background: "linear-gradient(180deg, #252624, transparent)",
          }}
          className="flex items-center pt-3 pb-4 px-1 sticky top-0"
        >
          <h1 className="font-space font-semibold text-md text-auto-white ml-2 -mt-0.5">
            Generated interface
          </h1>
        </div>
        {automation ? (
          <>
            <Interface inputObject={inputObject || []} />
            <div className="bg-auto-bg sticky bottom-0 mt-4">
              <button
                disabled={automationLoading}
                className={`w-full text-auto-white  bg-auto-white bg-opacity-40 shadow-md rounded-lg hover:bg-opacity-30 text-sm font-semibold py-2 ${
                  automationLoading ? "bg-opacity-30" : ""
                }`}
                onClick={() => createAutomationFromRecording(items)}
              >
                Run automation
              </button>
            </div>

            <div>
              <h1 className="font-space mt-12 mb-2 font-semibold text-md text-auto-white ml-2 -mt-0.5">
                Webhook
              </h1>
              <div className="rounded-xl  overflow-hidden relative items-center bg-auto-mid bg-opacity-80">
                <div className="bg-auto-light rounded-lg mt-2 mb-2  p-2 absolute z-10 shadow-sm  top-1 right-3">
                  <ClipboardCopy color={"white"} size={14} />
                </div>
                <SyntaxHighlighter
                  customStyle={{
                    backgroundColor: "rgba(0,0,0,0)",
                    fontSize: 12,
                  }}
                  language="uri"
                  style={darcula}
                >
                  https://automate.rectangle.com/skd84jbnbke2nd
                </SyntaxHighlighter>
              </div>

              <h1 className="font-space mt-6 mb-2 font-medium text-xs text-auto-white ml-2">
                POST example object
              </h1>
              <div className="rounded-xl  overflow-hidden relative items-center bg-auto-mid bg-opacity-80">
                <div className="bg-auto-light rounded-lg mt-2 mb-2  p-2 absolute z-10 shadow-sm  top-1 right-3">
                  <ClipboardCopy color={"white"} size={14} />
                </div>
                <SyntaxHighlighter
                  customStyle={{
                    backgroundColor: "rgba(0,0,0,0)",
                    fontSize: 12,
                  }}
                  language="json"
                  style={darcula}
                ></SyntaxHighlighter>
              </div>
            </div>
          </>
        ) : (
          <div className="flex flex-col space-around items-center w-full mt-20">
            <Sprout size={42} strokeWidth={1.5} color={"#474945"} />
            <p className="text-xs text-auto-light mt-4">
              We'll auto-generate a UI and webhooks for your automation
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
